import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ChainSettingsState,
  EnzymeRepository,
  EnzymeService,
  EnzymeState,
  EnzymeVault,
  SettingsRepository,
  VaultType,
} from 'common-rebalancing';
import {
  AlertService,
  ChainRepository,
  BLOCK_EXPLORER_ADDRESS_LINK,
  FrontendAddressUtil,
  TokenLogoComponent,
  ToggleInputComponent,
  WalletModalService,
} from 'common';
import { TranslateModule } from '@ngx-translate/core';
import { EnzymeSelectVaultModalComponent } from './enzyme-select-vault-modal/enzyme-select-vault-modal.component';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroPencilMini } from '@ng-icons/heroicons/mini';
import { CHAIN_ID } from '@31third/common';
import { Clipboard } from '@angular/cdk/clipboard';
import { NgxFloatUiModule } from 'ngx-float-ui';

@Component({
  selector: 'app-enzyme-vault-selection',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    EnzymeSelectVaultModalComponent,
    NgIcon,
    NgxFloatUiModule,
    TokenLogoComponent,
    ToggleInputComponent,
  ],
  providers: [
    provideIcons({
      heroPencilMini,
    }),
  ],
  templateUrl: './enzyme-vault-selection.component.html',
  styleUrls: ['./enzyme-vault-selection.component.css'],
})
export class EnzymeVaultSelectionComponent {
  getDisplayAddress = FrontendAddressUtil.getDisplayAddress;

  public isSelectVaultModalOpen = false;
  public isUseOwnerInsteadOfConnectedWalletSelectionOpen = false;
  public isSafe = false;

  constructor(
    private readonly enzymeRepository: EnzymeRepository,
    private readonly enzymeService: EnzymeService,
    private readonly chainRepository: ChainRepository,
    private readonly settingsRepository: SettingsRepository,
    private readonly clipboard: Clipboard,
    private readonly alertService: AlertService,
    private readonly walletModalService: WalletModalService,
  ) {
    this.subscribeToIsSafe();
  }

  private subscribeToIsSafe(): void {
    this.walletModalService
      .getIsSafeObservable()
      .subscribe(isSafe => (this.isSafe = !!isSafe));
  }

  public get enzymeState(): EnzymeState {
    return this.enzymeRepository.store.getValue();
  }

  public get chainSettingsState(): ChainSettingsState | undefined {
    return this.settingsRepository.getActiveChainSetting();
  }

  public onUseOwnerInsteadOfConnectedWalletChange(
    useOwnerInsteadOfConnectedWallet: boolean,
  ): void {
    this.enzymeRepository.setUseOwnerInsteadOfConnectedWallet(
      useOwnerInsteadOfConnectedWallet,
    );
  }

  public isSelected(): boolean {
    return this.enzymeState.selectedVault !== undefined;
  }

  public onSelectVaultClick(): void {
    this.isSelectVaultModalOpen = true;
  }

  public onVaultSelected(vault: EnzymeVault): void {
    void this.enzymeService.selectVault(vault);
  }

  public onCopyAddressClick(): void {
    if (this.enzymeState.selectedVault?.address) {
      this.clipboard.copy(this.enzymeState.selectedVault?.address);
      this.alertService.showInfo('global.copiedToClipboard');
    }
  }

  public getBlockExplorerLink(): string {
    const vault = this.enzymeState.selectedVault;
    if (!vault) {
      return '';
    }

    return BLOCK_EXPLORER_ADDRESS_LINK(
      vault.address,
      this.chainRepository.store.getValue().chain,
    );
  }

  public onBlockExplorerLinkClick(): void {
    window.open(this.getBlockExplorerLink(), '_blank');
  }

  public onReloadClick(): void {
    if (this.enzymeState.selectedVault) {
      void this.enzymeService.reloadVault(this.enzymeState.selectedVault);
    }
  }

  public getEnzymeLink(): string {
    const vault = this.enzymeState.selectedVault;
    if (!vault) {
      return '';
    }

    let network = '';
    if (vault.experimentalSettingsAdded) {
      network = '?network=testnet';
    } else if (
      this.chainRepository.store.getValue().chain?.identifier !==
      CHAIN_ID.ETHEREUM
    ) {
      network = `?network=${this.chainRepository.store
        .getValue()
        .chain?.name.toLowerCase()}`;
    }
    return `https://app.enzyme.finance/vault/${vault.address}${network}`;
  }

  public onEnzymeLinkClick(): void {
    window.open(this.getEnzymeLink(), '_blank');
  }

  public onClearClick(): void {
    this.enzymeRepository.resetSelectedVault();
  }

  public onToggleUseOwnerInsteadOfConnectedWalletSelectionOpenClick(): void {
    this.isUseOwnerInsteadOfConnectedWalletSelectionOpen =
      !this.isUseOwnerInsteadOfConnectedWalletSelectionOpen;
  }

  protected readonly PortfolioType = VaultType;
}
